// src/pages/RunAgentPage.jsx

import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { useWallet } from "../contexts/WalletContext";
import {
  getAgentById,
  runAgent as apiRunAgent,
} from "../services/agentService";
import Button from "../components/common/Button";
import Spinner from "../components/common/Spinner";
import {
  ArrowLeftIcon,
  PlayCircleIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
  CpuChipIcon,
  PaintBrushIcon,
  ChatBubbleLeftEllipsisIcon,
  DocumentChartBarIcon,
  ClipboardDocumentIcon, 
} from "@heroicons/react/24/outline";
import toast from 'react-hot-toast'; 

const IMAGE_AGENT_TYPE_ID_EXAMPLE = "08695708-e735-43f7-9fad-b04089d7801c"; 
const TEXT_AGENT_TYPE_ID_EXAMPLE = "66d4e15f-1951-43e7-afe9-99d571d707ca";

const getAgentTypeVisuals = (agentTypeNameOrId, agentTypeIdFromAgentObject) => {
  const idForIcon = agentTypeIdFromAgentObject || agentTypeNameOrId?.toLowerCase() || '';
  const name = agentTypeNameOrId?.toLowerCase() || ''; 
  let icon = <CpuChipIcon className="h-6 w-6 text-slate-400" />;
  let color = "bg-slate-700 text-slate-300";
  let accentColor = "border-slate-500";

  if (idForIcon === IMAGE_AGENT_TYPE_ID_EXAMPLE || name.includes('image') || name.includes('dall-e')) {
    icon = <PaintBrushIcon className="h-6 w-6 text-pink-400" />;
    color = "bg-pink-500/20 text-pink-300";
    accentColor = "border-pink-500";
  } else if (idForIcon === TEXT_AGENT_TYPE_ID_EXAMPLE || name.includes('text') || name.includes('chat') || name.includes('gpt')) {
    icon = <ChatBubbleLeftEllipsisIcon className="h-6 w-6 text-sky-400" />;
    color = "bg-sky-500/20 text-sky-300";
    accentColor = "border-sky-500";
  } else if (name.includes('chart') || name.includes('analyzer')) {
    icon = <DocumentChartBarIcon className="h-6 w-6 text-emerald-400" />;
    color = "bg-emerald-500/20 text-emerald-300";
    accentColor = "border-emerald-500";
  }
  return { icon, color, accentColor };
};

const RunAgentPage = () => {
  const { agentId } = useParams();
  const { currentAccount } = useWallet();

  const [agent, setAgent] = useState(null);
  const [userInput, setUserInput] = useState("");
  const [runResult, setRunResult] = useState(null);
  const [isLoadingAgent, setIsLoadingAgent] = useState(true);
  const [isRunning, setIsRunning] = useState(false);
  const [agentError, setAgentError] = useState("");
  const [runError, setRunError] = useState("");

  const fetchAgentDetails = useCallback(async () => {
    if (!agentId) return;
    setIsLoadingAgent(true);
    setAgentError("");
    setRunResult(null);
    setUserInput("");
    try {
      const data = await getAgentById(agentId, currentAccount);
      setAgent(data);
    } catch (error) {
      console.error("Failed to fetch agent details:", error);
      setAgentError(error.message || "Could not load agent details.");
    } finally {
      setIsLoadingAgent(false);
    }
  }, [agentId, currentAccount]);

  useEffect(() => {
    fetchAgentDetails();
  }, [fetchAgentDetails]);

  const handleRunAgent = async (e) => {
    e.preventDefault();
    if (!currentAccount) {
      setRunError("Wallet not connected. Please connect your wallet.");
      toast.error("Wallet not connected.");
      return;
    }
    if (!userInput.trim() && agent?.configuration?.serviceType !== "some_agent_type_that_needs_no_input") {
      setRunError("User input cannot be empty for this agent.");
      toast.error("User input cannot be empty.");
      return;
    }
    setIsRunning(true);
    setRunError("");
    setRunResult(null);
    const runningToastId = toast.loading("Running agent...");
    try {
      const result = await apiRunAgent(agentId, userInput, currentAccount);
      toast.dismiss(runningToastId);
      toast.success(result.message || "Agent run successfully!");
      setRunResult(result);
    } catch (error) {
      toast.dismiss(runningToastId);
      console.error("Failed to run agent:", error);
      const errorMessage = error.message || "An error occurred while running the agent.";
      toast.error(errorMessage);
      setRunError(errorMessage);
    } finally {
      setIsRunning(false);
    }
  };

  const copyToClipboard = (text, type = "Text") => {
    if (navigator.clipboard && text) {
      navigator.clipboard.writeText(text)
        .then(() => toast.success(`${type} copied to clipboard!`))
        .catch(err => {
          console.error('Failed to copy text: ', err);
          toast.error(`Failed to copy ${type.toLowerCase()}.`);
        });
    } else if (text) {
        try {
            const textArea = document.createElement("textarea");
            textArea.value = text;
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            toast.success(`${type} copied to clipboard! (fallback)`);
        } catch (err) {
            console.error('Fallback copy failed: ', err);
            toast.error(`Failed to copy ${type.toLowerCase()}.`);
        }
    }
  };


  let inputPlaceholder = "Enter your text prompt or input here...";
  let inputFieldType = "textarea";
  let inputRows = 6;

  if (agent?.configuration?.serviceType) {
    const serviceType = agent.configuration.serviceType.toLowerCase();
    if (serviceType.includes("image_url") || serviceType.includes("url")) {
      inputPlaceholder = "Enter URL for analysis (e.g., https://example.com/image.png)";
      inputFieldType = "url";
      inputRows = 1;
    } else if (serviceType.includes("json_data") || serviceType.includes("data_input")) {
      inputPlaceholder = 'Enter JSON data here (e.g., {"key": "value"})';
      inputRows = 10;
    } else if (serviceType.includes("image_generation")) {
      inputPlaceholder = "Describe the image you want to generate in detail...";
    }
  }

  // 1) Loading state
  if (isLoadingAgent) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  // 2) Fetch error
  if (agentError) {
    return (
      <div className="p-6 text-center text-red-400">
        <ExclamationTriangleIcon className="h-6 w-6 inline-block mr-2" />
        {agentError}
      </div>
    );
  }

  // 3) No agent found
  if (!agent) {
    return (
      <div className="p-6 text-center text-slate-500">
        Agent not found.
      </div>
    );
  }

  // … now agent is guaranteed non-null, safe to read agent.name below …
  const agentVisuals = getAgentTypeVisuals(
    agent.agent_type_name || agent.configuration?.serviceType,
    agent.agent_type_id
  );

  return (
    <div className="max-w-5xl mx-auto px-2 sm:px-0 pb-12">
      <div className="mb-6 mt-2 sm:mt-0">
        <Link to="/my-agents" className="inline-flex items-center text-sm text-blue-400 hover:text-blue-300 hover:underline transition-colors">
          <ArrowLeftIcon className="h-4 w-4 mr-1.5" />
          Back to My Agents
        </Link>
      </div>

      <div className={`bg-slate-800/80 backdrop-blur-xl shadow-2xl rounded-2xl border border-l-4 ${agentVisuals.accentColor} border-slate-700/60 overflow-hidden`}>
        <div className="p-6 sm:p-8 border-b border-slate-700/60">
            <div className="flex flex-col sm:flex-row sm:items-start sm:space-x-5 mb-3">
                <div className={`flex-shrink-0 p-3 rounded-lg mb-3 sm:mb-0 ${agentVisuals.color.replace('text-', 'bg-').replace('300', '500/20').replace('400', '500/20')}`}>
                {agentVisuals.icon}
                </div>
                <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-slate-50 mb-1 break-words">{agent.name}</h1>
                <span className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-full ${agentVisuals.color}`}>
                    {agent.agent_type_name || agent.configuration?.serviceType || "N/A"}
                </span>
                </div>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">{agent.description || "No description provided."}</p>
            <p className="text-xs text-slate-500 mt-2 font-mono">ID: {agent.agent_id}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
          <form onSubmit={handleRunAgent} className="p-6 sm:p-8 space-y-6 border-b md:border-b-0 md:border-r border-slate-700/60">
            <div>
              <label htmlFor="userInput" className="block text-lg font-semibold text-slate-200 mb-2">Agent Input</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                {inputFieldType === "textarea" ? (
                  <textarea id="userInput" name="userInput" rows={inputRows}
                    className="block w-full px-4 py-3 rounded-md sm:text-sm placeholder-slate-500 
                               bg-slate-900/50 border-2 border-slate-700 text-slate-100 
                               focus:ring-blue-500 focus:border-blue-500 transition"
                    placeholder={inputPlaceholder} value={userInput}
                    onChange={(e) => setUserInput(e.target.value)} disabled={isRunning}
                  />
                ) : (
                  <input id="userInput" name="userInput" type={inputFieldType}
                    className="block w-full px-4 py-3 rounded-md sm:text-sm placeholder-slate-500
                               bg-slate-900/50 border-2 border-slate-700 text-slate-100 
                               focus:ring-blue-500 focus:border-blue-500 transition"
                    placeholder={inputPlaceholder} value={userInput}
                    onChange={(e) => setUserInput(e.target.value)} disabled={isRunning}
                  />
                )}
              </div>
            </div>
            <div className="pt-2">
              <Button type="submit" variant="primary" isLoading={isRunning}
                disabled={isRunning || !currentAccount}
                className="w-full sm:w-auto px-8 py-3 text-base flex items-center justify-center bg-gradient-to-r from-blue-500 to-red-500 hover:from-blue-600 hover:to-red-600 border-transparent shadow-lg hover:shadow-red-500/30"
              >
                <PlayCircleIcon className="h-6 w-6 mr-2.5" />
                {isRunning ? "Running Agent..." : "Run Agent"}
              </Button>
              {!currentAccount && (
                <p className="mt-2 text-sm text-red-400">
                  Please connect your wallet to run this agent.
                </p>
              )}
            </div>
          </form>

          <div className="p-6 sm:p-8 min-h-[200px] flex flex-col">
            <h2 className="text-lg font-semibold text-slate-200 mb-3">Agent Output</h2>
            {isRunning && (
              <div className="flex items-center justify-center py-8">
                <Spinner size="lg" />
              </div>
            )}
            {runError && !isRunning && (
              <div className="flex items-center text-red-400 bg-red-900/30 border border-red-700 rounded-md p-3 mb-3">
                <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
                <span>{runError}</span>
              </div>
            )}
            
            {runResult && !isRunning && (
              <div className="bg-slate-900/70 border border-slate-700 text-slate-200 p-4 rounded-md overflow-x-auto text-sm min-h-[150px] max-h-[500px] custom-scrollbar flex-grow animate-fadeIn">
                {runResult.message && (
                  <p className="text-green-400 mb-3 text-xs italic">{runResult.message}</p>
                )}
                
                {runResult.result?.imageUrl && (
                  <div>
                    <img
                      src={runResult.result?.imageUrl}
                      alt={`Generated by ${agent.name}`}
                      className="max-w-full h-auto rounded-md border border-slate-600 mx-auto mb-2"
                    />
                  </div>
                )}

                {runResult.result?.text && (
                  <div className="relative">
                    <p className="whitespace-pre-wrap leading-relaxed">
                      {runResult.result.text}
                    </p>
                    <Button variant="ghost" size="xs" 
                            onClick={() => copyToClipboard(runResult.result.text, "Text")}
                            className="!text-sky-400 hover:!bg-sky-500/10 absolute top-0 right-0 -mt-1 -mr-1 opacity-75 hover:opacity-100">
                        <ClipboardDocumentIcon className="h-4 w-4"/>
                    </Button>
                  </div>
                )}
                
                {!runResult.result?.imageUrl && !runResult.result?.text && runResult.result && (
                  <div className="relative">
                    <pre className="whitespace-pre-wrap">{JSON.stringify(runResult.result, null, 2)}</pre>
                    <Button variant="ghost" size="xs" 
                            onClick={() => copyToClipboard(JSON.stringify(runResult.result, null, 2), "JSON")}
                            className="!text-sky-400 hover:!bg-sky-500/10 absolute top-0 right-0 -mt-1 -mr-1 opacity-75 hover:opacity-100">
                        <ClipboardDocumentIcon className="h-4 w-4"/>
                    </Button>
                  </div>
                )}

                {!runResult.result && !runResult.message && (
                    <p className="text-slate-500 italic">No specific output data received.</p>
                )}
              </div>
            )}
            {!runResult && !isRunning && !runError && (
                <div className="flex flex-col items-center justify-center h-full text-slate-500 py-10 border-2 border-dashed border-slate-700 rounded-lg opacity-75">
                    <InformationCircleIcon className="h-10 w-10 mb-2"/>
                    <p className="text-sm">Output will appear here.</p>
                </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RunAgentPage;